import React from 'react';
import { Linkedin, Instagram } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const IOS_APP_URL = 'https://apps.apple.com/app/go-nova/id6742720325';
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.gonova.app';

const Footer = () => {
    const { lang, changeLanguage } = useLanguage();

    // ScrollToTop handled globally in App.jsx

    // Hide Footer on Intranet pages
    if (location.pathname.startsWith('/gnm/admin/intranet')) {
        return null;
    }

    return (
        <footer style={{
            padding: 'var(--spacing-section) 0 40px',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            marginTop: '0'
        }}>
            <div className="container">
                {/* Top Section: Email & Navigation */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-section)',
                    marginBottom: 'var(--spacing-section)'
                }}>

                    {/* Contact Email - Typographic Style */}
                    <div>
                        <a href="mailto:hello@gonova.media" style={{
                            fontSize: 'clamp(1.5rem, 8vw, 4.5rem)',
                            fontWeight: 700,
                            color: 'white',
                            textDecoration: 'none',
                            letterSpacing: '-0.02em',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '16px',
                            transition: 'color 0.3s ease'
                        }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--color-accent-primary)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'white'}
                        >
                            hello@gonova.media
                        </a>
                    </div>

                    {/* Middle Section: Addresses & Links */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        gap: '40px',
                        paddingTop: 'var(--spacing-section)',
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                    }} className="footer-grid-responsive">
                        <style>{`
                            @media (min-width: 992px) {
                                .footer-grid-responsive {
                                    grid-template-columns: repeat(4, 1fr) !important;
                                }
                            }
                            @media (min-width: 600px) and (max-width: 991px) {
                                .footer-grid-responsive {
                                    grid-template-columns: repeat(2, 1fr) !important;
                                }
                            }
                        `}</style>

                        {/* Column 1: Istanbul */}
                        <div>
                            <p style={{
                                color: 'var(--color-text-tertiary)',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                marginBottom: '32px'
                            }}>
                                {lang === 'tr' ? 'Adresler' : 'Addresses'}
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '24px', color: 'white' }}>
                                Safter İş Merkezi, <br />
                                Büyükdere Caddesi Ecza Sk. <br />
                                No: 6/15 Kat: 5 Şişli <br />
                                İstanbul, 34394
                            </p>
                            <div style={{ display: 'flex', gap: '16px', fontSize: '0.9rem', opacity: 0.8 }}>
                                <a href="http://maps.apple.com/?q=Safter+Is+Merkezi+Istanbul" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent-secondary)', textDecoration: 'none', transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = 0.7} onMouseLeave={e => e.target.style.opacity = 1}>Apple Maps</a>
                                <span style={{ color: 'rgba(255,255,255,0.2)' }}>•</span>
                                <a href="https://www.google.com/maps/search/?api=1&query=Safter+Is+Merkezi+Istanbul" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent-primary)', textDecoration: 'none', transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = 0.7} onMouseLeave={e => e.target.style.opacity = 1}>Google Maps</a>
                            </div>
                        </div>

                        {/* Column 2: London */}
                        <div>
                            <p style={{
                                color: 'var(--color-text-tertiary)',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                marginBottom: '32px'
                            }}>
                                {"\u00A0"}
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '24px', color: 'white' }}>
                                Go Nova Holdings LTD <br />
                                71 Queen Victoria Street <br />
                                Floor 8 <br />
                                London, United Kingdom
                            </p>
                            <div style={{ display: 'flex', gap: '16px', fontSize: '0.9rem', opacity: 0.8 }}>
                                <a href="http://maps.apple.com/?q=71+Queen+Victoria+Street+London" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent-secondary)', textDecoration: 'none', transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = 0.7} onMouseLeave={e => e.target.style.opacity = 1}>Apple Maps</a>
                                <span style={{ color: 'rgba(255,255,255,0.2)' }}>•</span>
                                <a href="https://www.google.com/maps/search/?api=1&query=71+Queen+Victoria+Street+London" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent-primary)', textDecoration: 'none', transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = 0.7} onMouseLeave={e => e.target.style.opacity = 1}>Google Maps</a>
                            </div>
                        </div>

                        {/* Column 3: Product (Hidden on mobile to prioritize performance) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} className="hide-mobile">
                            <p style={{
                                color: 'var(--color-text-tertiary)',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                marginBottom: '32px'
                            }}>
                                {lang === 'tr' ? 'Ürün' : 'Product'}
                            </p>
                            <div style={{ display: 'flex', gap: '24px' }}>
                                {/* iOS Column */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        background: 'white',
                                        borderRadius: '12px',
                                        padding: '4px',
                                        position: 'relative',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                                    }}>
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(IOS_APP_URL)}`}
                                            alt="iOS QR"
                                            loading="lazy"
                                            decoding="async"
                                            style={{ width: '100%', height: '100%', mixBlendMode: 'multiply' }}
                                        />
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '50%', padding: '2px' }}>
                                            <img src="/brand/logo-emblem.png" alt="Go Nova" style={{ width: '14px', height: '14px' }} width="14" height="14" />
                                        </div>
                                    </div>
                                    <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer"
                                        style={{
                                            fontSize: '0.9rem',
                                            fontWeight: 400,
                                            color: 'var(--color-accent-secondary)',
                                            textDecoration: 'none',
                                            letterSpacing: '0.05em',
                                            transition: 'opacity 0.2s',
                                            opacity: 0.8
                                        }}
                                        onMouseEnter={e => e.target.style.opacity = 0.7}
                                        onMouseLeave={e => e.target.style.opacity = 0.8}
                                    >iOS</a>
                                </div>

                                {/* Android Column */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        background: 'white',
                                        borderRadius: '12px',
                                        padding: '4px',
                                        position: 'relative',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                                    }}>
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(ANDROID_APP_URL)}`}
                                            alt="Android QR"
                                            loading="lazy"
                                            decoding="async"
                                            style={{ width: '100%', height: '100%', mixBlendMode: 'multiply' }}
                                        />
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '50%', padding: '2px' }}>
                                            <img src="/brand/logo-emblem.png" alt="Go Nova" style={{ width: '14px', height: '14px' }} width="14" height="14" />
                                        </div>
                                    </div>
                                    <a href={ANDROID_APP_URL} target="_blank" rel="noopener noreferrer"
                                        style={{
                                            fontSize: '0.9rem',
                                            fontWeight: 400,
                                            color: 'var(--color-accent-primary)',
                                            textDecoration: 'none',
                                            letterSpacing: '0.05em',
                                            transition: 'opacity 0.2s',
                                            opacity: 0.8
                                        }}
                                        onMouseEnter={e => e.target.style.opacity = 0.7}
                                        onMouseLeave={e => e.target.style.opacity = 0.8}
                                    >Android</a>
                                </div>
                            </div>
                        </div>

                        {/* Column 4: Navigation (New structured column) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <p style={{
                                color: 'var(--color-text-tertiary)',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                marginBottom: '32px'
                            }}>
                                {lang === 'tr' ? 'Keşfet' : 'Explore'}
                            </p>
                            <ul style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                listStyle: 'none',
                                margin: 0,
                                padding: 0
                            }}>
                                <li><Link to={lang === 'tr' ? '/tr/about' : '/about'} style={{ fontSize: '1.2rem', color: 'white', textDecoration: 'none', transition: 'color 0.2s', fontWeight: 600 }} onMouseEnter={e => e.target.style.color = 'var(--color-accent-primary)'} onMouseLeave={e => e.target.style.color = 'white'}>{lang === 'tr' ? 'Hakkımızda' : 'About Us'}</Link></li>
                                <li><Link to={lang === 'tr' ? '/tr/services/mcn' : '/services/mcn'} style={{ fontSize: '1.2rem', color: 'white', textDecoration: 'none', transition: 'color 0.2s', fontWeight: 600 }} onMouseEnter={e => e.target.style.color = 'var(--color-accent-primary)'} onMouseLeave={e => e.target.style.color = 'white'}>MCN</Link></li>
                                <li><Link to={lang === 'tr' ? '/tr/services/distribution' : '/services/distribution'} style={{ fontSize: '1.2rem', color: 'white', textDecoration: 'none', transition: 'color 0.2s', fontWeight: 600 }} onMouseEnter={e => e.target.style.color = 'var(--color-accent-primary)'} onMouseLeave={e => e.target.style.color = 'white'}>{lang === 'tr' ? 'Dağıtım' : 'Content Distribution'}</Link></li>
                                <li><Link to={lang === 'tr' ? '/tr/brands' : '/brands'} style={{ fontSize: '1.2rem', color: 'white', textDecoration: 'none', transition: 'color 0.2s', fontWeight: 600 }} onMouseEnter={e => e.target.style.color = 'var(--color-accent-primary)'} onMouseLeave={e => e.target.style.color = 'white'}>{lang === 'tr' ? 'Markalar' : 'Our Brands'}</Link></li>
                                <li><Link to={lang === 'tr' ? '/tr/blog' : '/blog'} style={{ fontSize: '1.2rem', color: 'white', textDecoration: 'none', transition: 'color 0.2s', fontWeight: 600 }} onMouseEnter={e => e.target.style.color = 'var(--color-accent-primary)'} onMouseLeave={e => e.target.style.color = 'white'}>Blog</Link></li>
                                <li><Link to={lang === 'tr' ? '/tr/contact' : '/contact'} style={{ fontSize: '1.2rem', color: 'white', textDecoration: 'none', transition: 'color 0.2s', fontWeight: 600 }} onMouseEnter={e => e.target.style.color = 'var(--color-accent-primary)'} onMouseLeave={e => e.target.style.color = 'white'}>{lang === 'tr' ? 'İletişim' : 'Contact'}</Link></li>
                                <li>
                                    <a href="https://partner.gonova.media/login" target="_blank" rel="noopener noreferrer" style={{
                                        fontSize: '1.2rem',
                                        background: 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary))',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        textDecoration: 'none',
                                        transition: 'opacity 0.2s',
                                        fontWeight: 700,
                                        marginTop: '8px',
                                        display: 'inline-block'
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = 0.8}
                                        onMouseLeave={e => e.currentTarget.style.opacity = 1}>{lang === 'tr' ? 'Partner Girişi' : 'Partner Login'}</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bottom Section: Footer Meta */}
                <div className="footer-bottom-meta">
                    <style>{`
                        .footer-bottom-meta {
                            padding-top: 40px;
                            border-top: 1px solid rgba(255,255,255,0.05);
                            text-align: left;
                        }
                        .footer-meta-flex {
                            display: flex;
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 32px;
                            gap: 24px;
                        }
                        @media (max-width: 768px) {
                            .footer-bottom-meta { text-align: center; }
                            .footer-meta-flex { flex-direction: column; }
                            .footer-legal-links { flex-direction: column; justify-content: center; }
                            .footer-dot { display: none; }
                        }
                    `}</style>
                    <div className="footer-meta-flex">
                        <Link to="/">
                            <img 
                                src="/brand/logo-white.svg" 
                                alt="Go Nova Media" 
                                decoding="async" 
                                width="130" 
                                height="73" 
                                loading="lazy" 
                                style={{ width: '130px', opacity: 0.9 }} 
                            />
                        </Link>

                        {/* Social Assets */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <a href="https://www.linkedin.com/company/go-nova-media" target="_blank" rel="noopener noreferrer" style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                transition: 'color 0.3s, transform 0.3s',
                                border: '1px solid rgba(255,255,255,0.1)'
                            }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.color = 'black';
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                aria-label="LinkedIn Profile"
                            >
                                <Linkedin size={20} />
                            </a>
                            <a href="https://www.instagram.com/gonova.media/" target="_blank" rel="noopener noreferrer" style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                transition: 'color 0.3s, transform 0.3s',
                                border: '1px solid rgba(255,255,255,0.1)'
                            }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.color = 'black';
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                aria-label="Instagram Profile"
                            >
                                <Instagram size={20} />
                            </a>
                        </div>
                    </div>

                    <div className="footer-legal-links" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: '0.85rem',
                        marginBottom: '20px',
                        flexWrap: 'wrap'
                    }}>
                        <span>© 2026 {lang === 'tr' ? 'Go Nova Medya' : 'Go Nova Media'} Holdings LTD</span>
                        <span className="footer-dot" style={{ opacity: 0.2 }}>•</span>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center' }}>
                            <Link to={lang === 'tr' ? '/tr/privacy' : '/privacy'} style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = 'white'} onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}>{lang === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}</Link>
                            <span style={{ opacity: 0.2 }}>•</span>
                            <Link to="/brand-guidelines" style={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                textDecoration: 'none',
                                background: 'linear-gradient(90deg, #fff 0%, var(--color-accent-primary) 25%, var(--color-accent-secondary) 50%, var(--color-accent-primary) 75%, #fff 100%)',
                                backgroundSize: '200% auto',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                animation: 'brand-shimmer 20s infinite linear',
                                transition: 'opacity 0.2s'
                            }}
                                onMouseEnter={e => e.currentTarget.style.opacity = 0.8}
                                onMouseLeave={e => e.currentTarget.style.opacity = 1}
                            >{lang === 'tr' ? 'Marka Kimliği' : 'Brand Identity'}</Link>
                        </div>
                        <span className="footer-dot" style={{ opacity: 0.2 }}>•</span>
                        {/* Language Switcher */}
                        <button
                            onClick={() => changeLanguage(lang === 'en' ? 'tr' : 'en')}
                            style={{
                                background: 'transparent',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: '8px',
                                color: 'rgba(255,255,255,0.5)',
                                padding: '4px 12px',
                                cursor: 'pointer',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                letterSpacing: '0.12em',
                                transition: 'color 0.25s, border-color 0.25s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                            aria-label="Switch language"
                        >
                            {lang === 'en' ? 'TR' : 'EN'}
                        </button>
                    </div>

                    {/* Legal Disclaimer */}
                    <div style={{
                        fontSize: '0.7rem',
                        lineHeight: '1.6',
                        color: 'rgba(255,255,255,0.25)',
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                        paddingTop: '20px',
                        textAlign: 'justify',
                        maxWidth: '1000px'
                    }}>
                        {lang === 'tr'
                            ? 'Go Nova tarafından geliştirilen gonova.media ve mobil uygulamalarımızın tüm yazılım, tasarım ve fikri mülkiyet hakları münhasıran Go Nova\'ya aittir. Bu web sitesinde yer alan tüm içerikler yalnızca genel bilgilendirme amacı taşımakta olup, bağlayıcı bir teklif veya taahhüt niteliği taşımaz. Bilgilerin doğruluğu için azami özen gösterilmekle birlikte, Go Nova herhangi bir hata veya eksiklikten dolayı sorumluluk kabul etmez. Üçüncü taraf iş ortaklarına ait veri ve içeriklerin sorumluluğu ilgili taraflara aittir. Kullanılan tüm vaka çalışmaları ve örnekler, gerekli durumlarda anonimleştirilmiştir. Herhangi bir hata veya uygunsuz içerik tespit etmeniz halinde bizimle derhal iletişime geçmenizi rica ederiz. Bu siteye ait içeriklerin izinsiz kopyalanması, çoğaltılması veya dağıtılması kesinlikle yasaktır. Go Nova, web sitesi içeriğini ve bu politikayı önceden bildirimde bulunmaksızın değiştirme hakkını saklı tutar.'
                            : 'gonova.media and our mobile applications are developed in-house by Go Nova, and all software, design, and intellectual property rights belong exclusively to Go Nova. All content provided on this website is for general informational purposes only and does not constitute a binding legal offer or commitment. While we strive for accuracy, Go Nova assumes no liability for errors or omissions. Any data or information belonging to third-party partners is the sole responsibility of the respective parties. All case studies and examples used are anonymized where necessary. If you believe there is an error or inappropriate content, please contact us immediately. Unauthorized copying, reproduction, or distribution of any material from this site is strictly prohibited. Go Nova reserves the right to modify the website content and this policy at any time without prior notice.'
                        }
                    </div>
                    <style>{`
                        @keyframes brand-shimmer {
                            0% { background-position: 200% center; }
                            100% { background-position: -200% center; }
                        }
                    `}</style>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
